import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { shipmentsApi } from '../../api/shipments-api';
import { ErrorState } from '../../components/ErrorState';
import { PageLoadingState } from '../../components/PageLoadingState';
import { formatDate } from './site-data';

/** Shipments to or from the site, newest first. */
export function SiteShipmentsTab({ siteId }: { siteId: string }) {
  const query = useQuery({
    queryKey: ['shipments'],
    queryFn: () => shipmentsApi.listShipments(),
  });
  const shipments = useMemo(
    () =>
      (query.data?.shipments ?? [])
        .filter(
          (shipment) =>
            shipment.state !== 'CANCELLED' &&
            (shipment.destinationSiteId === siteId || shipment.origin?.siteId === siteId),
        )
        .sort((a, b) => b.shippedAt.localeCompare(a.shippedAt)),
    [query.data, siteId],
  );

  if (query.isLoading) return <PageLoadingState label="Loading shipments…" />;
  if (query.isError) {
    return (
      <ErrorState
        title="Shipments could not be loaded"
        rpcName="ListShipments"
        onRetry={() => void query.refetch()}
      />
    );
  }

  return (
    <section aria-label="Shipments to and from this site">
      {shipments.length === 0 ? (
        <p className="empty-note">No shipments to or from this site.</p>
      ) : (
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th scope="col">Consignment</th>
                <th scope="col">Direction</th>
                <th scope="col">Carrier</th>
                <th scope="col">Shipped</th>
                <th scope="col">Delivered</th>
              </tr>
            </thead>
            <tbody>
              {shipments.map((shipment) => (
                <tr key={shipment.id}>
                  <th scope="row">
                    <Link to={`/shipments/${encodeURIComponent(shipment.id)}`} className="mono">
                      {shipment.cargonizerConsignmentNumber}
                    </Link>
                  </th>
                  <td>
                    {shipment.destinationSiteId === siteId ? 'To this site' : 'From this site'}
                  </td>
                  <td>{shipment.carrier}</td>
                  <td className="mono">{formatDate(shipment.shippedAt)}</td>
                  <td className="mono">{formatDate(shipment.delivery?.deliveredAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
